import { useState, useEffect, useCallback } from "react";
import { useAdxQuery } from "../lib/useAdxQuery";
import { getChats, getChatMessages, getActivityMeta } from "../lib/apiClient";
import { formatTime, splitEmail, RANGES, rangePhrase } from "../lib/format";
import PageHead from "../components/PageHead";
import { Icon } from "../components/Icon";
import { ErrorAlert, Loading, Empty, Field } from "../components/States";

// The chat viewer offers no "Today" option; chats sync on a longer cadence.
const CHAT_RANGES = RANGES.filter(r => r.value !== "1");

function messageText(msg) {
  if (typeof msg.content === "string") return msg.content;
  if (Array.isArray(msg.content)) return msg.content.map(c => c.text || JSON.stringify(c)).join("\n");
  return JSON.stringify(msg.content);
}

function MessageThread({ messages }) {
  if (!messages || messages.length === 0) return <Empty title="No messages in this chat." />;
  return (
    <div className="thread">
      {messages.map((msg, i) => {
        const fromUser = msg.role === "user";
        return (
          <div key={msg.id ?? i} className={`msg ${fromUser ? "msg--user" : "msg--assistant"}`}>
            <div className="msg__label">
              {fromUser ? "User" : "Claude"}
              {msg.created_at && <span className="msg__time">{formatTime(msg.created_at)}</span>}
            </div>
            <div className="msg__body">{messageText(msg)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function ChatView() {
  const { run, loading, error } = useAdxQuery();
  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [actorEmail, setActor] = useState("");
  const [days, setDays] = useState("30");
  const [applied, setApplied] = useState(null);
  const [users, setUsers] = useState([]);

  const load = useCallback(async (f = { keyword, actorEmail, days }) => {
    setSelected(null); setDetail(null);
    const data = await run(getChats, { keyword: f.keyword, actorEmail: f.actorEmail, days: parseInt(f.days) });
    if (data) {
      setChats(data);
      setApplied({ filtered: Boolean(f.keyword || f.actorEmail), days: f.days });
    }
  }, [run, keyword, actorEmail, days]);

  const clearFilters = () => {
    setKeyword("");
    setActor("");
    load({ keyword: "", actorEmail: "", days });
  };

  useEffect(() => {
    load();
    run(getActivityMeta).then(d => { if (d) setUsers(d.users || []); });
  }, []); // eslint-disable-line

  const openChat = useCallback(async (chat) => {
    setSelected(chat); setDetailLoading(true);
    const data = await run(getChatMessages, chat.ChatId);
    if (data) setDetail(data);
    setDetailLoading(false);
  }, [run]);

  return (
    <>
      <PageHead
        viewId="chats"
        description="claude.ai conversations retrieved through the Compliance API. Only chats already fetched into the warehouse appear here; new chats sync every 15 minutes."
        actions={selected && (
          <button className="btn btn--secondary" type="button" onClick={() => { setSelected(null); setDetail(null); }}>
            <Icon name="back" size={16} /> Back to chats
          </button>
        )}
      />
      <div className="content">
        <ErrorAlert message={error} />
        {!selected ? (
          <>
            <form className="card filterbar" onSubmit={e => { e.preventDefault(); load(); }}>
              <div className="card__body filters">
                <Field id="chats-keyword" label="Title or content" grow>
                  <input id="chats-keyword" type="search" className="filterfield" placeholder="Keyword…"
                    value={keyword} onChange={e => setKeyword(e.target.value)} />
                </Field>
                <Field id="chats-user" label="User">
                  <input id="chats-user" type="text" className="filterfield" placeholder="user@eplp.com" list="chat-users"
                    autoComplete="off" value={actorEmail} onChange={e => setActor(e.target.value)} />
                  <datalist id="chat-users">
                    {users.map(u => <option key={u} value={u} />)}
                  </datalist>
                </Field>
                <Field id="chats-range" label="Time range">
                  <select id="chats-range" className="filterfield" value={days} onChange={e => setDays(e.target.value)}>
                    {CHAT_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </Field>
                <button className="btn btn--primary btn--sm" type="submit" disabled={loading}>
                  <Icon name="search" size={16} /> Search
                </button>
              </div>
            </form>

            {loading || (!applied && !error) ? (
              <Loading label="Loading chats…" />
            ) : chats.length === 0 ? (
              error ? null : applied.filtered ? (
                <Empty
                  title="No chats match these filters."
                  detail={`Nothing for that keyword or user in ${rangePhrase(applied.days)}.`}
                  action={<button className="btn btn--secondary btn--sm" type="button" onClick={clearFilters}>Clear filters</button>}
                />
              ) : (
                <Empty
                  title={`No fetched chats in ${rangePhrase(applied.days)}.`}
                  detail="Chats appear once the sync job has retrieved them from the Compliance API."
                />
              )
            ) : (
              <>
                <p className="summary">
                  <strong>{chats.length}</strong> chat{chats.length !== 1 ? "s" : ""} · newest first
                </p>
                <div className="grid-scroll">
                  <table className="grid">
                    <thead>
                      <tr><th>Fetched</th><th>Created</th><th>User</th><th>Title</th><th>Chat ID</th><th><span className="sr-only">Actions</span></th></tr>
                    </thead>
                    <tbody>
                      {chats.map(c => {
                        const { local, domain } = splitEmail(c.ActorEmail);
                        return (
                          <tr key={c.ChatId}>
                            <td className="nowrap cell-muted">{formatTime(c.FetchedAt)}</td>
                            <td className="nowrap cell-muted">{formatTime(c.CreatedAt)}</td>
                            <td>{local}{domain && <span className="cell-muted">@{domain}</span>}</td>
                            <td className="cell-truncate">{c.Title || "Untitled"}</td>
                            <td className="cell-muted cell-truncate">{c.ChatId}</td>
                            <td className="nowrap">
                              <button className="btn btn--ghost btn--sm" type="button" onClick={() => openChat(c)}
                                aria-label={`View chat: ${c.Title || "Untitled"}`}>
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="card">
            <div className="card__head chat__head">
              <div>
                <h2 className="chat__title">{detail?.Title || selected.Title || "Chat"}</h2>
                <p className="chat__meta">
                  {selected.ActorEmail} · {formatTime(selected.CreatedAt)} · {selected.ChatId}
                </p>
              </div>
              <span className="tag tag--new">Fetched {formatTime(selected.FetchedAt)}</span>
            </div>
            <div className="card__body">
              {detailLoading ? <Loading label="Loading messages…" /> : <MessageThread messages={detail?.Messages} />}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
